// 承認申請確認 (DRAFT → PENDING) / 承認確認 (PENDING → APPROVED、 AC-BSP-094) の
// 確認 modal に載せる集計・違反明細の組み立て (= 純関数)。
//
// 元は SpotBidding/index.tsx のローカル関数だったが、 承認 / 差戻しの状態と handler を
// `hooks/useSpotApprovalDecision.ts` へ切り出すのに合わせてここへ移した (= page と hook の
// 双方から使うため)。
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import type { BiddingBlockApprovalRequest } from '@ebalance/proto/bffuser/v1/bidding_block_model_pb'
import { BiddingBlockBidStatus } from '@ebalance/proto/bffuser/v1/bidding_block_model_pb'
import {
  BiddingApprovalState,
  BiddingBidSendStatus,
  BiddingSpotBidStatus,
} from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import type { TFunction } from 'i18next'

import type { ViolationSection } from '~/components/bidding/common/SubmitConfirmModal'
import { JEPX_AREA_CODES, convertAreaLabel } from '~/models/area'
import type { BidLimits } from '~/models/bidLimits'
import { resolveAreaSideLimit } from '~/models/bidLimits'

import { expandBlockLegSlots } from './blockBidLegSlots'
import type { SpotBidRow } from './spotBidRow'
import type {
  VolumeCountableBid,
  VolumeTotalViolation,
  VolumeTotalViolationScene,
} from './spotBidValidation'
import {
  JEPX_STAIRS_LIMIT,
  detectInversions,
  detectJepxLimitViolations,
  detectLimitViolations,
  detectStairsExcesses,
  detectVolumeTotalViolations,
  formatJepxLimitViolationDetail,
  formatLimitViolationDetail,
  formatStairsExcessDetail,
  formatVolumeTotalViolationDetail,
} from './spotBidValidation'

/**
 * 違反明細を出す確認 modal の場面。 同じ検出結果でも「次に取るべき操作」 が申請者と
 * 承認者で違うため、 解消手順の文言 (body) を出し分ける:
 *   - request: 承認申請確認。 申請者が入札一覧で該当行を直す (AC-BSP-008)
 *   - submit: 直接提出確認 (承認なし・自動のエリア、 AC-BSP-015)。 申請者が直すのは
 *     request と同じで、 上限数量(コマ計) の明細・本文だけ「提出できません」 になる (AC-BSP-063)
 *   - mixed: 承認申請と提出が混ざる確認 (AC-BSP-015)。 入札制限 (1 札・コマ計) の本文と
 *     コマ計の明細が「申請・提出できません」 になる (design `spot.html` の確認モーダル)
 *   - approve: 承認確認。 承認者は札を直せないので「差戻し」 で申請者に返す (AC-BSP-097 ※節)
 */
export type SpotViolationAudience = VolumeTotalViolationScene

/**
 * 受渡日内の札から 1 つの申請セット (request) の札だけを切り出す (= 申請取消 / 承認の
 * 確認 modal が対象 request の札のみを集計するため、 AC-BSP-055 / AC-BSP-094 / AC-BSP-082)。
 * request 未選択 (undefined) は空。
 */
export const selectRequestBids = (
  rows: SpotBidRow[],
  requestId: string | undefined,
): SpotBidRow[] =>
  requestId === undefined
    ? []
    : rows.filter((b) => b.approvalRequestId === requestId)

/** 確認 modal 用の集計 (= 札件数 / 売買別 数量合計 MW)。 */
export type SpotBidSummary = {
  count: number
  /** 買 数量合計 MW (= コマ別 MW の単純合算、 AC-BSP-053)。 */
  buyMw: number
  /** 売 数量合計 MW。 */
  sellMw: number
}

/**
 * 確認 modal 用の集計 (= 札件数 / 売買別 数量合計) を行群から算出する。
 *
 * 単位は MW のまま合算する (= MWh へ換算しない)。 AC-BSP-053 [P0] が「申請内容サマリを
 * 含む全画面で MW に統一、 合計欄も MW で MWh には換算しない」 と定め、 issue #6266 で
 * PdM が 2026-08-28 に MW で確定した。 行の保持がコマ別 MW なので `volumeMwh` を
 * そのまま足す (field 名は proto 由来で、 値は MW。 issue #4285)。
 */
export const summarizeSpotBidRows = (rows: SpotBidRow[]): SpotBidSummary => {
  let buyMw = 0
  let sellMw = 0
  for (const b of rows) {
    const v = Number(b.volumeMwh)
    if (b.direction === Side.BUY) buyMw += v
    else if (b.direction === Side.SELL) sellMw += v
  }
  return { count: rows.length, buyMw, sellMw }
}

/** 確認 modal のサマリに出すエリア 1 件ぶんの内訳。 */
export type SpotAreaBreakdownRow = {
  areaCode: AreaCode
  areaLabel: string
  count: number
  /** 買 数量合計 MW (= 合計行と同じ単位、 AC-BSP-053)。 */
  buyMw: number
  /** 売 数量合計 MW。 */
  sellMw: number
}

/**
 * 承認確認 modal の「エリア別内訳」 (AC-BSP-097)。 札が 1 件も無いエリアは行を作らない
 * (= 全 9 エリアを常に並べると、 単一エリアの申請でも内訳が 9 行になり読めない)。
 *
 * 並び順は `JEPX_AREA_CODES` (= 北海道 → 九州の系統順) に揃える。 札の出現順に並べると
 * 同じ申請でも明細 Get の解決順で内訳の並びが変わってしまう。 数量は合計行と同じく
 * MW の単純合算 (AC-BSP-053、 `summarizeSpotBidRows` 参照)。
 */
export const buildSpotAreaBreakdown = (
  rows: SpotBidRow[],
): SpotAreaBreakdownRow[] => {
  const byArea = new Map<
    AreaCode,
    { count: number; buy: number; sell: number }
  >()
  for (const b of rows) {
    const entry = byArea.get(b.areaCode) ?? { count: 0, buy: 0, sell: 0 }
    entry.count += 1
    const v = Number(b.volumeMwh)
    if (b.direction === Side.BUY) entry.buy += v
    else if (b.direction === Side.SELL) entry.sell += v
    byArea.set(b.areaCode, entry)
  }
  const out: SpotAreaBreakdownRow[] = []
  for (const code of JEPX_AREA_CODES) {
    const entry = byArea.get(code)
    if (entry === undefined) continue
    out.push({
      areaCode: code,
      areaLabel: convertAreaLabel(code),
      count: entry.count,
      buyMw: entry.buy,
      sellMw: entry.sell,
    })
  }
  return out
}

/**
 * 承認時の入札制限再検証 (AC-BSP-097) で母集合に含める承認状態。
 *
 * AC-BSP-080 が定める「有効札」 (下書きの申請対象 + 承認申請中 + 承認済 + 入札中) のうち、
 * 承認時点で JEPX に到達しうるもの = 承認申請中 / 承認済 / 入札中。 「下書きの申請対象」 は
 * 承認申請時 (AC-BSP-008) にこれから申請する下書きを指し、 承認時には対象セットが既に
 * PENDING になっているので該当しない。
 *
 * 未申請の下書き (AC-BSP-079 で別セットに積まれた追加入札) は**含めない**。 JEPX に
 * 到達しない札の分まで階段数に載せて承認を止めると、 承認者に用意された救済 (差戻し) では
 * 解消できず (違反の出所が別セットの下書き)、 締切までに承認できなければ失効する
 * (mock-app `JepxSpotBid.Alerts.cs` も「見えも直せもしない札の違反で詰ませない」 方針)。
 * その下書きが申請される時に AC-BSP-008 が本セット (PENDING 以降) を含めて判定するので、
 * 超過は申請側で止まる。 取消 (CANCELLED) / 失効・送信失敗 (FAILED) / 差戻し (REJECTED) も
 * 同じく JEPX へ到達しないので除外する (= 取消枠は解放する)。
 */
const REVALIDATION_APPROVAL_STATES: ReadonlySet<BiddingApprovalState> = new Set(
  [
    BiddingApprovalState.PENDING,
    BiddingApprovalState.APPROVED,
    BiddingApprovalState.SUBMITTED,
  ],
)

/**
 * JEPX への到達を確定的に諦めた送信状態 (= `sendStatus` 軸)。
 *
 * `approvalState` が SUBMITTED のまま据え置かれても (`spotBidRow.ts` の sendStatus
 * コメント参照)、 送信取消 (CANCELED) や確定送信失敗 (SEND_FAILED_FINAL) の札は
 * JEPX 板に乗らないので取消枠として解放する必要がある (REVALIDATION_APPROVAL_STATES の
 * 上の godoc「取消/失効/差戻しは除外」と同じ理由、承認軸だけでは判別できない分をこちらで
 * 補う)。 一時的な送信失敗 (SEND_FAILED、 再送中) はまだ到達し得るので除外しない。
 */
const DEAD_END_SEND_STATUSES: ReadonlySet<BiddingBidSendStatus> = new Set([
  BiddingBidSendStatus.SEND_FAILED_FINAL,
  BiddingBidSendStatus.CANCELED,
])

/**
 * 受渡日内の札から、 承認時の再検証母集合 (AC-BSP-097 / AC-BSP-080) を抜き出す。
 * 承認申請後に積み増されて**申請済** (PENDING 以降) になった追加入札 (AC-BSP-079) は
 * ここに入り、 未申請の下書きは入らない (理由は REVALIDATION_APPROVAL_STATES)。
 * 送信軸で JEPX 到達を確定的に諦めた札も除く (理由は DEAD_END_SEND_STATUSES)。
 * 入札中 (SUBMITTED) の札は約定軸が未確定 (bidStatus PENDING) のものだけを残す
 * (AC-BSP-080「約定確定 (全約定 / 部分約定 / 未約定) の札は合算母集合から除外」。
 * backend の SumActiveSpotBidVolume と同じ述語)。
 */
export const selectRevalidationBids = (rows: SpotBidRow[]): SpotBidRow[] =>
  rows.filter(
    (b) =>
      REVALIDATION_APPROVAL_STATES.has(b.approvalState) &&
      (b.sendStatus === undefined ||
        !DEAD_END_SEND_STATUSES.has(b.sendStatus)) &&
      (b.approvalState !== BiddingApprovalState.SUBMITTED ||
        b.bidStatus === BiddingSpotBidStatus.PENDING),
  )

// 入札中の leg のうち JEPX の板に載っているもの。 取下処理中・取下失敗は板に残るので
// 数え、 取消と約定確定は外す (AC-BSP-080 / AC-BSP-081)。
const IS_ON_BOARD_BY_BLOCK_BID_STATUS: Record<BiddingBlockBidStatus, boolean> =
  {
    [BiddingBlockBidStatus.UNSPECIFIED]: false,
    [BiddingBlockBidStatus.PENDING]: true,
    [BiddingBlockBidStatus.MATCHED]: false,
    [BiddingBlockBidStatus.UNMATCHED]: false,
    [BiddingBlockBidStatus.FAILED]: false,
    [BiddingBlockBidStatus.EXPIRED]: false,
    [BiddingBlockBidStatus.CANCELLED]: false,
    [BiddingBlockBidStatus.WITHDRAWING]: true,
    [BiddingBlockBidStatus.WITHDRAW_FAILED]: true,
  }

/**
 * 受渡日のブロック入札から、 上限数量(コマ計) の合算母集合に加えるコマ別数量を抜き出す
 * (AC-BSP-080「ブロック入札の当該コマ数量を含む」、 bid-limits.md §1-2、 issue #8229)。
 *
 * 有効の判定は通常入札の `selectRevalidationBids` と同じ述語を group に当てる
 * (承認申請中・承認済・入札中、 送信を確定的に諦めた group は除く)。 入札中の leg は
 * 板に載っているもの ([[IS_ON_BOARD_BY_BLOCK_BID_STATUS]]) だけを数える (backend の
 * SumActiveBlockBidVolume と同じ母集合)。 leg はコマ別に数量が異なり得る
 * (AC-BSP-030a) ので、 time slot 1 件を 1 entry に展開する。
 */
export const selectRevalidationBlockVolumes = (
  requests: readonly BiddingBlockApprovalRequest[],
): VolumeCountableBid[] =>
  expandBlockLegSlots(
    requests.filter(
      (g) =>
        REVALIDATION_APPROVAL_STATES.has(g.approvalState) &&
        !DEAD_END_SEND_STATUSES.has(g.sendStatus),
    ),
    (g, leg) =>
      g.approvalState !== BiddingApprovalState.SUBMITTED ||
      IS_ON_BOARD_BY_BLOCK_BID_STATUS[leg.bidStatus],
  )

/**
 * 同一コマ × エリア × 売買方向のグルーピングキー (= 階段数の集計単位、 AC-BSP-021)。
 *
 * 発注主体 (受渡契約コード / `issuerType`) はキーに含めていない。 AC-BSP-021 [P1] は
 * 上限 50 を「EN 実装時に JEPX 公式仕様を確認、 暫定」 としており、 集計単位が会員単位か
 * 受渡契約コード単位かも未確認 (= 受渡契約コード単位なら、 別主体の札を合算している現状は
 * 過検出側)。 承認ブロック (AC-BSP-097 [P0]) の条件に格上げしているため、 公式仕様の確認は
 * issue #6594 の残債として明示している。 `detectStairsExcesses` (spotBidValidation.ts) も
 * 同じキーで集計する。
 */
export const stairsKeyOf = (
  timeCode: number,
  areaCode: AreaCode,
  side: Side,
): string => `${timeCode}|${areaCode}|${side}`

/**
 * 対象の (エリア × 売買) のどれかに上限数量(コマ計) が設定されているか。 母集合に足せない札が
 * ある時、 結論が変わりうる (= fail-closed 節を立てる) かの判定に使う。
 */
export const hasVolumeTotalLimit = (
  targets: ReadonlyArray<{ areaCode: AreaCode; direction: Side }>,
  limits: BidLimits,
): boolean =>
  targets.some(
    (x) =>
      resolveAreaSideLimit(limits, x.areaCode, x.direction)?.qtyTotalMax !==
      undefined,
  )

/**
 * 確認 modal に積む違反明細セクション群 (= 検出結果のみ)。 承認申請確認 (DRAFT →
 * PENDING) と承認確認 (PENDING → APPROVED、 AC-BSP-094 / AC-BSP-097) で同じ 3 種を出す。
 *
 * - AC-BSP-050 JEPX 制度上限違反: 価格 0.01〜999 円/kWh、数量 0.1〜9999.9 MW の
 *   制度値域。テナント設定に依存せず全組織で効く。AC-BSP-052 が「テナント設定上限違反
 *   とは独立した違反種別として区別して表示する」ことを求めるため、下の入札制限違反とは
 *   別セクションで出す (= 同じ札が両方に該当したら 2 セクションに出る)。
 * - AC-008 入札制限違反 (= テナント設定): 判定は一覧アラート (NormalBidTab) と同じ
 *   settings「入札制限」実設定値ベース (= useBidLimits、connect-query cache 共有で
 *   追加 fetch なし)。一覧アラートが表示中エリアに絞って数えるのに対し、確認 modal は
 *   対象申請セットの全札が対象なのでエリアフィルタを掛けない (= 非表示エリアの違反札を
 *   素通しさせない)。
 * - AC-021 JEPX 階段数超過: 同一コマ × エリア × 売買方向の札数が上限を超えるグループ。
 * - AC-080 上限数量(コマ計) 超過 (= テナント設定 QTY_TOTAL 軸): 同一 (エリア×コマ×売買)
 *   の有効札合計が settings「入札制限」の上限数量(コマ計) を超えるグループ (AC-BSP-080 /
 *   AC-BSP-097)。 上限数量(1札) (上の AC-008 セクション) とは別軸・別文言 (AC-BSP-063) の
 *   ため独立したセクションで出す。
 *
 * ## 母集合を複数に分ける理由 (AC-BSP-097)
 *
 * 上 2 種 (JEPX 制度上限・上限数量(1札)) は**札 1 件ずつ**の判定なので、 対象の札
 * (`rows`) だけを見れば足りる。 他の申請セットの札を混ぜると、 この承認では手当てできない
 * 違反で承認がブロックされる (spec が示す救済は「差戻して申請者に直させる」 なので、
 * 対象外の札の違反を出しても差戻し先が違う)。
 *
 * 階段数超過と上限数量(コマ計) 超過は**同一コマ × エリア × 売買の集計**判定で、 承認申請後に
 * 積み増された追加入札 (AC-BSP-079) で事後的に超過しうる。 そのため `stairsRows` /
 * `volumeTotalRows` には受渡日の有効札全体 (`selectRevalidationBids`) を渡し、 検出した
 * グループのうち**対象の札が 1 件でも属するもの**だけを残す (= 対象と無関係なコマの超過で
 * 承認を止めない)。 いずれも省略時は `rows` 自身が母集合 (= 承認申請確認 modal の従来挙動。
 * `volumeTotalRows` 省略時の呼び出し元は「承認申請確認 modal 側で別途 母集合を組んで渡す」
 * 設計、 `SpotBidding/index.tsx` の `draftViolationSections` 参照)。
 */
export const buildSpotViolationSections = (
  rows: SpotBidRow[],
  limits: BidLimits,
  t: TFunction,
  options?: {
    audience?: SpotViolationAudience
    /** 階段数超過の集計母集合 (= 省略時は `rows`)。 */
    stairsRows?: SpotBidRow[]
    /** 上限数量(コマ計) 超過の集計母集合 (= 省略時は `rows`、 AC-BSP-080 / AC-BSP-097)。 */
    volumeTotalRows?: readonly VolumeCountableBid[]
    /**
     * `volumeTotalRows` から検出済みの上限数量(コマ計) 超過。 渡すと母集合の再集計を省く
     * (= 同じ母集合を案件の数だけ集計し直さないため。 まとめて承認が案件ごとに呼ぶ)。
     */
    volumeTotalViolations?: readonly VolumeTotalViolation[]
  },
): ViolationSection[] => {
  const audience = options?.audience ?? 'request'
  const stairsRows = options?.stairsRows
  const volumeTotalRows = options?.volumeTotalRows
  // 解消手順の文言 key は宛先ごとに実キーを書き出す (= 動的組み立てにすると i18n key の
  // grep / 未使用検出が効かなくなる)。
  const bodyKeys =
    audience === 'approve'
      ? {
          jepx: 'page:biddingSpot.violation.jepxBodyApprove',
          limit: 'page:biddingSpot.violation.limitBodyApprove',
          stairs: 'page:biddingSpot.violation.stairsBodyApprove',
          volumeTotal: 'page:biddingSpot.violation.volumeTotalBodyApprove',
        }
      : {
          jepx: 'page:biddingSpot.violation.jepxBody',
          limit:
            audience === 'mixed'
              ? 'page:biddingSpot.violation.limitBodyMixed'
              : 'page:biddingSpot.violation.limitBody',
          stairs: 'page:biddingSpot.violation.stairsBody',
          volumeTotal: {
            request: 'page:biddingSpot.violation.volumeTotalBody',
            submit: 'page:biddingSpot.violation.volumeTotalBodySubmit',
            mixed: 'page:biddingSpot.violation.volumeTotalBodyMixed',
          }[audience],
        }
  const jepxDetails = detectJepxLimitViolations(rows).map(
    formatJepxLimitViolationDetail,
  )
  const limitDetails = detectLimitViolations(rows, limits).map((v) =>
    formatLimitViolationDetail(v, audience),
  )
  const groupScope = new Set(
    rows.map((b) => stairsKeyOf(b.timeCode, b.areaCode, b.direction)),
  )
  // stairsRows / volumeTotalRows 省略時は母集合 = rows なので、 検出したグループは
  // 必ず groupScope に含まれる (= この filter は母集合を広げた時だけ効く)。
  const stairsDetails = detectStairsExcesses(stairsRows ?? rows)
    .filter((e) => groupScope.has(stairsKeyOf(e.timeCode, e.areaCode, e.side)))
    .map((e) => formatStairsExcessDetail(e))
  const volumeTotalDetails = (
    options?.volumeTotalViolations ??
    detectVolumeTotalViolations(volumeTotalRows ?? rows, limits)
  )
    .filter((v) => groupScope.has(stairsKeyOf(v.timeCode, v.areaCode, v.side)))
    .map((v) => formatVolumeTotalViolationDetail(v, audience))
  return [
    {
      title: t('page:biddingSpot.violation.jepxTitle', {
        count: jepxDetails.length,
      }),
      body: t(bodyKeys.jepx),
      details: jepxDetails,
    },
    {
      title: t('page:biddingSpot.violation.limitTitle', {
        count: limitDetails.length,
      }),
      body: t(bodyKeys.limit),
      details: limitDetails,
    },
    {
      title: t('page:biddingSpot.violation.stairsTitle', {
        count: stairsDetails.length,
      }),
      body: t(bodyKeys.stairs, {
        limit: JEPX_STAIRS_LIMIT,
      }),
      details: stairsDetails,
    },
    {
      title: t('page:biddingSpot.violation.volumeTotalTitle', {
        count: volumeTotalDetails.length,
      }),
      body: t(bodyKeys.volumeTotal),
      details: volumeTotalDetails,
    },
  ]
}

/**
 * 承認申請確認 / 承認確認 modal に積む売買逆転チェック結果 (AC-BSP-007 / AC-BSP-097 が
 * 「入札制限チェック結果, 売買逆転チェック結果」 を列挙)。 spec §2 が「警告表示するが
 * 意図的な場合もあるためブロックはしない」 と定めるので、 呼び出し元は
 * `SubmitConfirmModal.warningSections` (= hasViolation に寄与しない) に積む。
 *
 * 逆転は (エリア × コマ) 内の 買 max ≧ 売 min で決まり、 対象セットの買と他セットの売の
 * 組でも JEPX 上は逆転になる。 そのため階段数と同じく `populationRows` (受渡日の有効札全体、
 * `selectRevalidationBids`) で判定し、 検出した組のうち**対象の札が 1 件でも巻き込まれて
 * いるもの**だけを残す (= 対象と無関係な組の逆転で承認者を惑わせない)。 一覧側の逆転
 * アラート (NormalBidTab) は表示中エリアに絞って数えるため、 複数エリアに跨る申請セットの
 * 逆転は一覧では一度も提示されない — 確認 modal はエリアフィルタを掛けない。
 *
 * 解消手順の文言 (body) は宛先で出し分ける (`buildSpotViolationSections` と同じ
 * `SpotViolationAudience`)。 逆転はどちらの宛先でもブロックしないため、 差は「申請/承認は
 * ブロックしない」 の主語だけ (= 承認者は札を直せないので「差戻し」 を促す他の違反種別とは
 * 異なり、 逆転に専用の解消操作は無い)。
 */
export const buildSpotInversionSection = (
  rows: SpotBidRow[],
  populationRows: SpotBidRow[],
  t: TFunction,
  audience: SpotViolationAudience = 'request',
): ViolationSection => {
  const targetIds = new Set(rows.map((b) => b.id))
  // 対象セットが母集合に含まれていない呼び出し方 (= 母集合が別状態で絞られている) でも
  // 対象同士の逆転を落とさないよう、 id で重複を除いて合成する。
  const byId = new Map<string, SpotBidRow>()
  for (const b of [...populationRows, ...rows]) byId.set(b.id, b)
  const details = detectInversions([...byId.values()])
    .filter((inv) => inv.bidIds.some((id) => targetIds.has(id)))
    .sort((a, b) => a.timeCode - b.timeCode || a.areaCode - b.areaCode)
    .map((inv) => {
      const head = t('page:biddingSpot.violation.inversionHead', {
        timeCode: inv.timeCode,
        area: convertAreaLabel(inv.areaCode),
      })
      return inv.hasMarketOrder ||
        inv.maxBuyPrice === undefined ||
        inv.minSellPrice === undefined
        ? t('page:biddingSpot.violation.inversionDetailMarket', { head })
        : t('page:biddingSpot.violation.inversionDetail', {
            head,
            maxBuy: inv.maxBuyPrice.toFixed(2),
            minSell: inv.minSellPrice.toFixed(2),
          })
    })
  return {
    title: t('page:biddingSpot.violation.inversionTitle', {
      count: details.length,
    }),
    body: t(
      audience === 'approve'
        ? 'page:biddingSpot.violation.inversionBodyApprove'
        : 'page:biddingSpot.violation.inversionBody',
    ),
    details,
  }
}

/**
 * 入札制限 (settings「入札制限」) を取得できていない時に積む fail-closed セクション。
 *
 * 取得失敗のまま上の AC-008 判定を通すと「制限未設定 = 違反なし」 に見えるだけで検証が
 * 成立していない (= 空 lookup)。 「違反なし」 と「検証できていない」 を確認 modal 上で
 * 区別し、 確定ボタンを非活性にするために節を 1 つ立てる。
 *
 * 承認側でも同じ扱いにする (AC-BSP-097 が承認実行時の再検証を [P0] で要求しているため、
 * 検証が成立していない状態で承認を通すとその AC を素通りさせることになる)。
 */
export const buildBidLimitsUnavailableSection = (
  t: TFunction,
  audience: SpotViolationAudience = 'request',
): ViolationSection => {
  if (audience === 'approve') {
    return {
      title: t('page:biddingSpot.violation.limitUnavailableApproveTitle'),
      body: t('page:biddingSpot.violation.limitUnavailableApproveBody'),
      details: [t('page:biddingSpot.violation.limitUnavailableApproveDetail')],
    }
  }
  return {
    title: t('page:biddingSpot.violation.limitUnavailableTitle'),
    body: t('page:biddingSpot.violation.limitUnavailableBody'),
    details: [t('page:biddingSpot.violation.limitUnavailableDetail')],
  }
}

/**
 * ブロック入札を取得できていない時に積む、 上限数量(コマ計) の fail-closed セクション。
 *
 * 上限数量(コマ計) の合算母集合には有効なブロック入札の当該コマ数量が入る (AC-BSP-080)。
 * ブロック入札の一覧・明細を確定できていない間にそのまま判定すると、 ブロック分を 0 として
 * 「違反なし」 に見えるだけで検証が成立していない (= 入札制限を取得できない時の
 * `buildBidLimitsUnavailableSection` と同じ構図)。 そのため対象の札のうち 1 件でも
 * 上限数量(コマ計) が設定された (エリア × 売買) に属していれば節を 1 つ立てて確定を止める。
 * 設定が無ければブロック分の有無で結論が変わらないので立てない。
 */
export const buildBlockVolumesUnavailableSections = (
  rows: readonly SpotBidRow[],
  limits: BidLimits,
  t: TFunction,
  audience: SpotViolationAudience = 'request',
): ViolationSection[] => {
  if (!hasVolumeTotalLimit(rows, limits)) return []
  return [
    {
      title: t('page:biddingSpot.violation.blockVolumeUnavailableTitle'),
      body: t(
        audience === 'approve'
          ? 'page:biddingSpot.violation.blockVolumeUnavailableApproveBody'
          : 'page:biddingSpot.violation.blockVolumeUnavailableBody',
      ),
      details: [t('page:biddingSpot.violation.blockVolumeUnavailableDetail')],
    },
  ]
}

/** 承認確認の違反明細・注意の組み立てに要る、 案件に依らない前提。 */
export type SpotApprovalCheckContext = {
  /** 階段数・売買逆転の母集合 (= 受渡日内の全札を `selectRevalidationBids` で絞ったもの)。 */
  revalidationBids: SpotBidRow[]
  /** 上限数量(コマ計) の再検証母集合 (通常入札 + ブロック入札の当該コマ数量)。 */
  volumeTotalRows: readonly VolumeCountableBid[]
  /** `volumeTotalRows` から検出済みの超過 (省略時は案件ごとに検出する)。 */
  volumeTotalViolations?: readonly VolumeTotalViolation[]
  limits: BidLimits
  /** 入札制限を判定に使えないか (取得失敗。 呼び出し元によっては取得中も含む)。 再検証が成立しないので fail-closed。 */
  isLimitsUnavailable: boolean
  /** ブロック入札の一覧 / 明細が未解決か (= 上限数量(コマ計) が fail-closed)。 */
  isBlockVolumeUnresolved: boolean
}

/**
 * 承認確認 (PENDING → APPROVED、 AC-BSP-097) で 1 つの申請セットに積む違反明細・判定できない
 * 節・売買逆転の注意。 単件の承認確認 modal (`useSpotApprovalDecision`) と、 案件ごとに同じ
 * 検証を見せるまとめて承認 (AC-BSP-118) が共有する唯一の組み立て (= fail-closed の節を足す
 * 規則を 2 か所に写さない)。
 *
 * 判定できない節 (`unverifiedSections`) を違反と分けて返すのは、 まとめて承認が「違反」 は
 * 送れて「判定できない」 は送れない (= 確定を止める) ため。 単件はどちらでも確定を止める。
 */
export const buildSpotApprovalChecks = (
  approvingBids: SpotBidRow[],
  ctx: SpotApprovalCheckContext,
  t: TFunction,
): {
  violationSections: ViolationSection[]
  unverifiedSections: ViolationSection[]
  warningSections: ViolationSection[]
} => {
  const { revalidationBids } = ctx
  return {
    violationSections: buildSpotViolationSections(
      approvingBids,
      ctx.limits,
      t,
      {
        audience: 'approve',
        stairsRows: revalidationBids,
        // 上限数量(コマ計) の再検証母集合も階段数と同じ「受渡日の有効札全体」
        // (AC-BSP-097 / AC-BSP-080)。 承認対象の札は既に PENDING なので volumeTotalRows に
        // 含まれる (= 自集約の二重計上にはならない)。
        volumeTotalRows: ctx.volumeTotalRows,
        volumeTotalViolations: ctx.volumeTotalViolations,
      },
    ),
    unverifiedSections: [
      // 入札制限を取得できていない間は「違反なし」 と「検証できていない」 が区別できない。
      // AC-BSP-097 [P0] が承認実行時の再検証を求めている以上 fail-closed にする。
      ...(ctx.isLimitsUnavailable
        ? [buildBidLimitsUnavailableSection(t, 'approve')]
        : []),
      ...(ctx.isBlockVolumeUnresolved
        ? buildBlockVolumesUnavailableSections(
            approvingBids,
            ctx.limits,
            t,
            'approve',
          )
        : []),
    ],
    warningSections: [
      buildSpotInversionSection(approvingBids, revalidationBids, t, 'approve'),
    ],
  }
}
